import { effectiveGridSize, type Viewport } from '@kanvas/board-core';

/* Same violet-tinted neutral the chrome is built from, so the grid reads as
   part of the board rather than as a grey sheet under it. */
const DOT_COLOR = 'rgba(19, 20, 44, 0.2)';
const LINE_COLOR = 'rgba(19, 20, 44, 0.06)';
const LINE_COLOR_MAJOR = 'rgba(19, 20, 44, 0.12)';

function modPositive(n: number, m: number): number {
  const r = n % m;
  return r < 0 ? r + m : r;
}

/**
 * Grid painting in screen space.
 *
 * Iterating in board coordinates used to hang the tab once the camera was far
 * enough that `bx += size` no longer changed `bx` (float64). Screen pixels
 * stay in a few-thousand range no matter how far the user pans.
 *
 * The pitch still adapts to zoom (see `effectiveGridSize`) so the grid never
 * turns into a solid fill when zoomed out. Dots are 1px rects rather than
 * arcs: at a few thousand dots per frame, `arc` + `fill` costs measurably more
 * than `fillRect`, and at this size the shape is indistinguishable.
 */
export function paintGrid(
  ctx: CanvasRenderingContext2D,
  vp: Viewport,
  mode: 'none' | 'dots' | 'lines',
  baseSize: number,
): void {
  if (mode === 'none') return;

  const size = effectiveGridSize(baseSize, vp.zoom, mode === 'dots' ? 12 : 8);
  const step = size * vp.zoom;
  if (!Number.isFinite(step) || step < 4) return;

  const originX = modPositive(-vp.x * vp.zoom, step);
  const originY = modPositive(-vp.y * vp.zoom, step);
  const colLimit = Math.ceil(vp.width / step) + 3;
  const rowLimit = Math.ceil(vp.height / step) + 3;

  if (mode === 'dots') {
    const dot = vp.zoom > 2 ? 2 : 1.5;
    ctx.save();
    ctx.fillStyle = DOT_COLOR;
    for (let col = 0, sx = originX; col < colLimit; col++, sx += step) {
      const x = Math.round(sx);
      for (let row = 0, sy = originY; row < rowLimit; row++, sy += step) {
        const y = Math.round(sy);
        ctx.fillRect(x - dot / 2, y - dot / 2, dot, dot);
      }
    }
    ctx.restore();
    return;
  }

  ctx.save();
  ctx.lineWidth = 1;
  for (let col = 0, sx = originX; col < colLimit; col++, sx += step) {
    const x = Math.round(sx) + 0.5;
    const boardX = vp.x + sx / vp.zoom;
    const major = Math.round(boardX / size) % 5 === 0;
    ctx.strokeStyle = major ? LINE_COLOR_MAJOR : LINE_COLOR;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, vp.height);
    ctx.stroke();
  }
  for (let row = 0, sy = originY; row < rowLimit; row++, sy += step) {
    const y = Math.round(sy) + 0.5;
    const boardY = vp.y + sy / vp.zoom;
    const major = Math.round(boardY / size) % 5 === 0;
    ctx.strokeStyle = major ? LINE_COLOR_MAJOR : LINE_COLOR;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(vp.width, y);
    ctx.stroke();
  }
  ctx.restore();
}
